import { Option } from '@sapphire/result';

import { roundToDecimals } from '@ps/utils';

import type { SupplierAllocation } from '../models/supplier-allocation.model';

export interface NavbarCpiInputs {
  readonly priceOverride: number | null;
  readonly feasibilityCpi: number;
  readonly allocations: readonly SupplierAllocation[];
}

/**
 * The navbar CPI, resolved by priority: a set global price override wins, then a
 * real feasibility CPI, and only when neither exists do we blend the allocations.
 */
export function resolveNavbarCpi(inputs: NavbarCpiInputs): number {
  return Option.from(inputs.priceOverride).match({
    some: (override) => override,
    none: () =>
      inputs.feasibilityCpi > 0
        ? inputs.feasibilityCpi
        : averageAllocationCpi(inputs.allocations),
  });
}

/**
 * Blended CPI: the simple mean of the active allocations' CPIs, counting only
 * rows that carry a real (non-zero) CPI in the denominator. Mirrors legacy
 * `getTotalCpi` — active = `quantity > 0` (the old `selected`), and a zero-CPI
 * row is not "valid".
 */
export function averageAllocationCpi(
  allocations: readonly SupplierAllocation[],
): number {
  const activeAllocations = allocations.filter(
    (allocation) => allocation.quantity > 0,
  );
  const totalCpi = activeAllocations.reduce(
    (sum, allocation) => sum + allocation.cpi,
    0,
  );
  if (!totalCpi) return 0;
  const validCpiCount = activeAllocations.filter(
    (allocation) => allocation.cpi,
  ).length;
  return roundToDecimals(totalCpi / validCpiCount);
}
